import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Copy, Loader2, FileText, Pencil, Check, X, Download, Share2,
  ChevronDown, FileType2, ExternalLink, Wand2,
} from "lucide-react";

export const SCRIPT_TYPES = [
  { key: "video",  label: "Video script",       tint: "tint-blue-soft",     dot: "var(--blue-deep)",     deep: "var(--blue-deep)"     },
  { key: "email",  label: "Email / sales copy", tint: "tint-matcha-soft",   dot: "var(--matcha-deep)",   deep: "var(--matcha-deep)"   },
  { key: "social", label: "Social post",        tint: "tint-yellow-soft",   dot: "var(--yellow-deep)",   deep: "var(--yellow-deep)"   },
  { key: "sales",  label: "Long-form sales",    tint: "tint-pink-soft",     dot: "var(--pink-deep)",     deep: "var(--pink-deep)"     },
];
export const TINT_BY_TYPE = Object.fromEntries(SCRIPT_TYPES.map((t) => [t.key, t]));

export default function CurrentScriptCard({ script, onUpdated, clientName }) {
  const nav = useNavigate();
  const [editing, setEditing] = useState(false);
  const [editContent, setEditContent] = useState(script.content);
  const [editTopic, setEditTopic] = useState(script.topic);
  const [saving, setSaving] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [sharing, setSharing] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    const onDoc = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setExportOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const t = TINT_BY_TYPE[script.script_type] || SCRIPT_TYPES[0];

  const startEdit = () => { setEditContent(script.content); setEditTopic(script.topic); setEditing(true); };

  const saveEdit = async () => {
    if (editContent.trim().length === 0) { toast.error("Content cannot be empty"); return; }
    setSaving(true);
    try {
      const { data } = await api.patch(`/scripts/${script.id}`, { content: editContent, topic: editTopic });
      onUpdated(data);
      setEditing(false);
      toast.success("Script updated");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to update"); }
    finally { setSaving(false); }
  };

  const copy = async () => {
    try { await navigator.clipboard.writeText(script.content); toast.success("Copied to clipboard"); }
    catch { toast.error("Couldn't copy — select the text manually"); }
  };

  const remix = () => {
    nav(`/clients/${script.client_id}/generate?remix=${script.id}`);
  };

  const downloadBlob = (filename, data, mime) => {
    const blob = new Blob([data], { type: mime });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = filename; document.body.appendChild(a); a.click();
    a.remove(); URL.revokeObjectURL(url);
  };

  const safeName = () => {
    const base = (script.topic || "script").replace(/[^a-z0-9-_ ]/gi, "").replace(/\s+/g, "-").toLowerCase();
    return `${base}-${script.script_type}`.slice(0, 60);
  };

  const exportTxt = () => {
    const body = `${script.topic}\n\n${script.content}\n\n— Generated by Scriptora for ${clientName} · ${script.model}`;
    downloadBlob(`${safeName()}.txt`, body, "text/plain;charset=utf-8");
    setExportOpen(false); toast.success("Downloaded .txt");
  };

  const exportPdf = async () => {
    setExportOpen(false);
    try {
      const { jsPDF } = await import("jspdf");
      const doc = new jsPDF({ unit: "pt", format: "letter" });
      const margin = 56;
      const width = doc.internal.pageSize.getWidth() - margin * 2;
      const height = doc.internal.pageSize.getHeight() - margin * 2;
      let y = margin;
      doc.setFont("times", "bold"); doc.setFontSize(22);
      const titleLines = doc.splitTextToSize(script.topic || "Untitled script", width);
      doc.text(titleLines, margin, y); y += titleLines.length * 26 + 6;
      doc.setFont("courier", "normal"); doc.setFontSize(9); doc.setTextColor(120);
      doc.text(`${script.script_type.toUpperCase()} · ${script.model} · ${clientName}`, margin, y); y += 24;
      doc.setTextColor(30);
      doc.setFont("times", "normal"); doc.setFontSize(12);
      const paragraphs = script.content.split(/\n{2,}/);
      const lineHeight = 16;
      for (const para of paragraphs) {
        const lines = doc.splitTextToSize(para, width);
        for (const line of lines) {
          if (y > height + margin) { doc.addPage(); y = margin; }
          doc.text(line, margin, y); y += lineHeight;
        }
        y += lineHeight * 0.5;
      }
      doc.save(`${safeName()}.pdf`);
      toast.success("Downloaded .pdf");
    } catch { toast.error("PDF export failed"); }
  };

  const exportGoogleDocs = async () => {
    setExportOpen(false);
    try {
      const body = `${script.topic}\n\n${script.content}`;
      await navigator.clipboard.writeText(body);
      window.open("https://docs.google.com/document/create", "_blank", "noopener");
      toast.success("Script copied — paste into the new Google Doc that opened");
    } catch { toast.error("Couldn't open Google Docs"); }
  };

  const shareLink = async () => {
    setExportOpen(false);
    setSharing(true);
    try {
      const { data } = await api.post(`/scripts/${script.id}/share`);
      onUpdated(data);
      const url = `${window.location.origin}/s/${data.share_token}`;
      try { await navigator.clipboard.writeText(url); }
      catch (err) { console.warn("Clipboard write failed", err); }
      toast.success("Share link copied to clipboard", { description: url, duration: 6000 });
    } catch { toast.error("Couldn't create link"); }
    finally { setSharing(false); }
  };

  return (
    <div className={`${t.tint} hair rounded-md p-8 rise-in`} data-testid="current-script" style={{ borderColor: t.deep }}>
      <div className="flex items-start justify-between mb-4 gap-4 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="mono-label flex items-center gap-2">
            <span className="dot" style={{ background: t.dot }} /> {script.script_type} · {script.model}
          </div>
          {editing ? (
            <input
              data-testid="edit-topic-input"
              value={editTopic}
              onChange={(e) => setEditTopic(e.target.value)}
              className="font-serif-display text-2xl mt-1 bg-transparent w-full border-b border-black/20 focus:outline-none focus:border-black/50 py-1"
            />
          ) : (
            <div className="font-serif-display text-2xl mt-1" data-testid="current-script-topic">{script.topic}</div>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0 flex-wrap">
          {editing ? (
            <>
              <button data-testid="save-edit-button" onClick={saveEdit} disabled={saving}
                className="btn-primary inline-flex items-center gap-1 text-sm px-3 py-2 rounded-md">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Save
              </button>
              <button data-testid="cancel-edit-button" onClick={() => setEditing(false)}
                className="btn-ghost inline-flex items-center gap-1 text-sm px-3 py-2 rounded-md">
                <X className="w-4 h-4" /> Cancel
              </button>
            </>
          ) : (
            <>
              <button data-testid="remix-script-button" onClick={remix}
                className="btn-ghost hair inline-flex items-center gap-1 text-sm px-3 py-2 rounded-md" style={{ background: "var(--paper)" }}
                title="Duplicate this brief with a new angle">
                <Wand2 className="w-4 h-4" /> Remix
              </button>
              <button data-testid="edit-script-button" onClick={startEdit}
                className="btn-ghost inline-flex items-center gap-1 text-sm px-3 py-2 rounded-md">
                <Pencil className="w-4 h-4" /> Edit
              </button>
              <button data-testid="copy-current-script" onClick={copy}
                className="btn-ghost inline-flex items-center gap-1 text-sm px-3 py-2 rounded-md">
                <Copy className="w-4 h-4" /> Copy
              </button>
              <div className="relative" ref={menuRef}>
                <button data-testid="export-menu-toggle" onClick={() => setExportOpen((v) => !v)}
                  className="btn-primary inline-flex items-center gap-1 text-sm px-3 py-2 rounded-md">
                  <Download className="w-4 h-4" /> Export <ChevronDown className="w-3 h-3" />
                </button>
                {exportOpen && (
                  <div className="absolute right-0 mt-2 w-56 hair rounded-md shadow-lg z-20 rise-in"
                    style={{ background: "var(--paper)" }} data-testid="export-menu">
                    <MenuItem testid="export-pdf" onClick={exportPdf} icon={<FileType2 className="w-4 h-4" />} label="Download PDF" />
                    <MenuItem testid="export-gdocs" onClick={exportGoogleDocs} icon={<ExternalLink className="w-4 h-4" />} label="Open in Google Docs" />
                    <MenuItem testid="export-txt" onClick={exportTxt} icon={<FileText className="w-4 h-4" />} label="Download .txt" />
                    <div className="hair-t" />
                    <MenuItem testid="export-share" onClick={shareLink} disabled={sharing}
                      icon={<Share2 className="w-4 h-4" />}
                      label={sharing ? "Creating link…" : (script.share_token ? "Copy share link" : "Create share link")} />
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {editing ? (
        <textarea
          data-testid="edit-content-textarea"
          value={editContent}
          onChange={(e) => setEditContent(e.target.value)}
          rows={Math.min(30, Math.max(10, editContent.split("\n").length + 2))}
          className="w-full hair rounded-md p-4 script-body focus-primary resize-y"
          style={{ background: "var(--paper)" }}
        />
      ) : (
        <div className="script-body" data-testid="current-script-content">{script.content}</div>
      )}

      {script.share_token && !editing && (
        <div className="mt-4 mono-label flex items-center gap-2" data-testid="share-link-indicator">
          <Share2 className="w-3 h-3" /> Public link active · /s/{script.share_token}
        </div>
      )}
    </div>
  );
}

function MenuItem({ onClick, icon, label, testid, disabled }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      disabled={disabled}
      className="w-full text-left flex items-center gap-2 px-3 py-2 text-sm hover:bg-[var(--surface-hover)] transition-colors duration-200 disabled:opacity-50"
    >
      {icon} {label}
    </button>
  );
}
